import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { pagesAPI, ehrAPI, shiftsAPI } from '../utils/api';
import { ArrowLeft, AlertCircle, UserCheck } from 'lucide-react';

const CreatePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [ehrLoading, setEhrLoading] = useState(false);
  const [ehrData, setEhrData] = useState(null);
  const [error, setError] = useState('');
  const [onDutyDoctors, setOnDutyDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState('');
  
  const [formData, setFormData] = useState({
    patient: {
      nhsNumber: '',
      age: '',
      location: ''
    },
    requestType: 'clinical_review',
    reason: '',
    clinicalUrgency: 'standard',
    estimatedDuration: 15,
    assignedTo: ''
  });

  useEffect(() => {
    fetchOnDutyDoctors();
    const interval = setInterval(fetchOnDutyDoctors, 10000); // Refresh every 10s
    return () => clearInterval(interval);
  }, []);

  const fetchOnDutyDoctors = async () => {
    try {
      const response = await shiftsAPI.getOnDutyDoctors();
      setOnDutyDoctors(response.data);
      // Auto-select first doctor if none selected
      if (response.data.length > 0 && !selectedDoctor) {
        setSelectedDoctor(response.data[0].id);
        setFormData(prev => ({ ...prev, assignedTo: response.data[0].id }));
      }
    } catch (error) {
      console.error('Error fetching on-duty doctors:', error);
    }
  };

  const handleNHSLookup = async () => {
    if (!formData.patient.nhsNumber) return;
    
    setEhrLoading(true);
    try {
      const response = await ehrAPI.getPatientFlags(formData.patient.nhsNumber);
      if (response.data.found) {
        setEhrData(response.data);
        setFormData(prev => ({
          ...prev,
          patient: {
            ...prev.patient,
            age: response.data.age
          }
        }));
      } else {
        setError('Patient not found in EHR system');
      }
    } catch (err) {
      setError('Error fetching patient data');
    } finally {
      setEhrLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!selectedDoctor) {
      setError('Please select a doctor to assign this message');
      setLoading(false);
      return;
    }

    try {
      const response = await pagesAPI.create({ ...formData, assignedTo: selectedDoctor });
      navigate('/nurse');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create message');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 sm:gap-4 mb-2">
            <img src="/REACH_LOGO.png" alt="Reach Logo" className="h-10 sm:h-12 w-auto" />
            <button
              onClick={() => navigate('/nurse')}
              className="flex items-center gap-1 sm:gap-2 text-gray-600 hover:text-gray-900 text-sm sm:text-base"
            >
              <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="hidden sm:inline">Back to Dashboard</span>
              <span className="sm:hidden">Back</span>
            </button>
          </div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Create New Message</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 space-y-6">
          {/* Patient Details */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Patient Details</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  URN *
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={formData.patient.nhsNumber}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      patient: { ...prev.patient, nhsNumber: e.target.value }
                    }))}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="123-456-789"
                    required
                  />
                  <button
                    type="button"
                    onClick={handleNHSLookup}
                    disabled={ehrLoading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {ehrLoading ? 'Loading...' : 'Lookup'}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Age
                </label>
                <input
                  type="number"
                  value={formData.patient.age}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    patient: { ...prev.patient, age: e.target.value }
                  }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Location (Ward/Bed) *
                </label>
                <input
                  type="text"
                  value={formData.patient.location}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    patient: { ...prev.patient, location: e.target.value }
                  }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Ward 4, Bed 12"
                  required
                />
              </div>
            </div>
          </div>

          {/* EHR Alerts */}
          {ehrData && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-900 mb-2">⚠️ Patient Alerts from EHR</h3>
              {ehrData.allergies && ehrData.allergies.length > 0 && (
                <p className="text-sm text-blue-800">• Allergies: {ehrData.allergies.join(', ')}</p>
              )}
              {ehrData.medications.highRisk && ehrData.medications.highRisk.length > 0 && (
                <p className="text-sm text-blue-800">• High-risk medications: {ehrData.medications.highRisk.join(', ')}</p>
              )}
              {ehrData.flags.onAnticoagulation && (
                <p className="text-sm text-blue-800">• On anticoagulation therapy</p>
              )}
            </div>
          )}

          {/* Request Details */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Request Details</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Request Type *
                </label>
                <select
                  value={formData.requestType}
                  onChange={(e) => setFormData(prev => ({ ...prev, requestType: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="clinical_review">Clinical Review</option>
                  <option value="medication">Medication Review/Prescription</option>
                  <option value="procedure">Procedure</option>
                  <option value="administrative">Administrative</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for Message *
                </label>
                <textarea
                  value={formData.reason}
                  onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  rows="5"
                  placeholder="Describe the clinical situation, symptoms, vital signs, etc."
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Clinical Urgency *
                </label>
                <select
                  value={formData.clinicalUrgency}
                  onChange={(e) => setFormData(prev => ({ ...prev, clinicalUrgency: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="life_threatening">Life-threatening (immediate review)</option>
                  <option value="urgent">Urgent (within 30 minutes)</option>
                  <option value="standard">Standard (within 2 hours)</option>
                  <option value="routine">Routine (when convenient)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Doctor Assignment */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Assign to Doctor</h2>
            
            {onDutyDoctors.length === 0 ? (
              <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg">
                <p className="font-medium">⚠️ No doctors currently on duty</p>
                <p className="text-sm mt-1">Please wait for a doctor to start their shift before creating a message.</p>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center gap-2 mb-3">
                  <UserCheck className="w-5 h-5 text-green-600" />
                  <span className="text-sm text-gray-600">{onDutyDoctors.length} doctor(s) on duty</span>
                </div>
                {onDutyDoctors.map(doctor => (
                  <label
                    key={doctor.id}
                    className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      selectedDoctor === doctor.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="doctor"
                      value={doctor.id}
                      checked={selectedDoctor === doctor.id}
                      onChange={(e) => {
                        setSelectedDoctor(e.target.value);
                        setFormData(prev => ({ ...prev, assignedTo: e.target.value }));
                      }}
                      className="mr-3"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{doctor.name}</p>
                      <p className="text-sm text-gray-600">{doctor.title} - {doctor.department}</p>
                      {doctor.specialties && doctor.specialties.length > 0 && (
                        <p className="text-xs text-gray-500 mt-1">{doctor.specialties.join(', ')}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        On duty since {new Date(doctor.shiftStartTime).toLocaleTimeString()}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              {error}
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
            >
{loading ? 'Creating Message...' : 'Submit Message'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/nurse')}
              className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default CreatePage;
